import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type LuminarySceneImageRenderingObserver = {
    onRenderingStart: (sceneId: string) => void;
    onRenderingFinish: (sceneId: string) => void;
    onImageDestroy: (sceneId: string) => void;
};

type Result = {
    renderingIsInProgress: boolean;
    renderingObserver: LuminarySceneImageRenderingObserver;
};

/**
 * This hook works with LuminarySceneImage to keep track of which scenes are currently in the process of rendering.
 * It can be used to display a loading indicator when rendering is in progress, avoiding the pitfalls
 * of manually listening to slide events and checking luminary scene queue.
 *
 * You can group scenes by type and display a loading indicator for groups independently.
 * Or, you can render the same scene with different resolutions and display a loading indicator while both are in progress.
 *
 * By default, renderingIsInProgressInitialState is false, but you can set it to true if you want to start with a loading indicator and you know that rendering will start soon.
 */
export function useLuminarySceneImageRenderingObserver(
    renderingIsInProgressInitialState = false,
): Result {
    const renderingStatuses = useRef<Record<string, boolean>>({});
    const [renderingIsInProgress, setRenderingIsInProgress] = useState(
        renderingIsInProgressInitialState,
    );
    const renderingIsInProgressRef = useRef(renderingIsInProgressInitialState);

    useEffect(() => {
        renderingIsInProgressRef.current = renderingIsInProgressInitialState;
        setRenderingIsInProgress(renderingIsInProgressInitialState);
    }, [renderingIsInProgressInitialState]);

    const update = useCallback(() => {
        const newRenderingIsInProgress = Object.values(renderingStatuses.current).some(
            (status) => status === true,
        );
        const oldRenderingIsInProgress = renderingIsInProgressRef.current;

        // HACK: Despite react declaring that it will not trigger a re-render if the state is the same, it does so in this case
        // I have not reproduced it in a simple example, so I'm not sure what's causing it
        // but if you remove ref and always call setRenderingIsInProgress, it will see more renders in outer component

        if (newRenderingIsInProgress !== oldRenderingIsInProgress) {
            renderingIsInProgressRef.current = newRenderingIsInProgress;
            setRenderingIsInProgress(newRenderingIsInProgress);
        }
    }, [setRenderingIsInProgress]);

    const renderingObserver = useMemo(() => {
        return {
            onRenderingStart: (renderId: string) => {
                renderingStatuses.current[renderId] = true;
                update();
            },
            onRenderingFinish: (renderId: string) => {
                renderingStatuses.current[renderId] = false;
                update();
            },
            onImageDestroy: (renderId: string) => {
                delete renderingStatuses.current[renderId];
                update();
            },
        };
    }, [update]);

    return {
        renderingIsInProgress,
        renderingObserver,
    };
}
